/**
 * PLAT-176: the router seam for `@olytic/foundation`.
 *
 * Foundation never imports a router (its `AGENTS.md`; olytic-infra
 * `studio-app-platform.md` §12). It asks for navigation in two shapes:
 *
 * - `AppShell`, `AppSwitcher` and the record patterns take a
 *   `linkComponent: NavLinkComponent`. `RouterNavLink` is Workspace's.
 * - Other components take `href` plus `onClick`. `useLinkProps(to)` returns
 *   that pair, so a plain click navigates client-side and every other click
 *   (new tab, new window, download, middle click) stays the browser's.
 *
 * Nothing renders through this file yet: the shell story (PLAT-168) is its
 * first user. It replaces nothing either; `components/UiLink.tsx` stays until
 * the last story of the epic retires it.
 *
 * The decisions are pure functions (`toRouterPath`, `shouldInterceptClick`,
 * `createLinkClickHandler`) so they are unit-tested without a DOM.
 */

import type { NavLinkComponent, NavLinkProps } from '@olytic/foundation';
import { forwardRef, type MouseEvent as ReactMouseEvent } from 'react';
import { Link, useHref, useNavigate } from 'react-router-dom';

/**
 * The in-app path for an href, or `null` when the href leaves the app.
 *
 * Anything with a URL scheme (`https:`, `http:`, `mailto:`, `tel:`, ...) or a
 * protocol-relative `//host` is external: it is never handed to the router and
 * its clicks are never intercepted, even when it happens to point back at this
 * origin. Everything else (`/connectors`, `settings`, `?tab=x`, `#top`) is a
 * router path, passed through verbatim so react-router resolves it exactly as
 * it would a `<Link to>`.
 */
export function toRouterPath(href: string): string | null {
  const trimmed = href.trim();
  if (trimmed.startsWith('//')) return null;
  // RFC 3986 scheme: a letter, then letters, digits, "+", "-" or ".", then ":".
  if (/^[a-z][a-z\d+.-]*:/i.test(trimmed)) return null;
  return href;
}

/** The parts of a click the interception decision reads. */
export interface LinkClickInfo {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
}

/**
 * Whether a click on a link should become a client-side navigation.
 *
 * Only a plain primary click on a link that opens in this browsing context:
 * no Cmd/Ctrl (new tab), Shift (new window), Alt (download), no middle or
 * right button, not already handled by another listener, and no `target`
 * other than `_self`. The same rule react-router's own `<Link>` applies, so
 * `RouterNavLink` and `useLinkProps` links behave alike.
 */
export function shouldInterceptClick(event: LinkClickInfo, target?: string | null): boolean {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  return !target || target === '_self';
}

/** A click as `createLinkClickHandler` sees it: a React or DOM mouse event. */
export interface LinkClickEvent extends LinkClickInfo {
  preventDefault(): void;
  /** The element the handler is on; its `target` attribute counts. */
  currentTarget?: { getAttribute(name: string): string | null } | null;
}

export interface LinkClickOptions {
  /**
   * The link's `target`. When omitted, the clicked element's `target`
   * attribute is read instead, so `<a target="_blank">` falls through either
   * way.
   */
  target?: string;
}

/**
 * The `onClick` for a link to `href`: on a click `shouldInterceptClick`
 * accepts, prevent the browser's navigation and call `navigate` with the
 * router path. External hrefs and every other click are left alone.
 */
export function createLinkClickHandler(
  href: string,
  navigate: (path: string) => void,
  options: LinkClickOptions = {},
): (event: LinkClickEvent) => void {
  const path = toRouterPath(href);
  return (event) => {
    if (path === null) return;
    const target = options.target ?? event.currentTarget?.getAttribute('target') ?? null;
    if (!shouldInterceptClick(event, target)) return;
    event.preventDefault();
    navigate(path);
  };
}

export interface LinkProps {
  href: string;
  onClick: (event: ReactMouseEvent<HTMLElement>) => void;
}

/**
 * `{ href, onClick }` for a Foundation component that takes them (a TextLink,
 * a Button rendered as a link). `href` is the real URL, so hover, copy-link
 * and open-in-new-tab work; `onClick` turns a plain click into
 * `navigate(to)`. An external `to` gets its href verbatim and an `onClick`
 * that does nothing.
 */
export function useLinkProps(to: string, options: LinkClickOptions = {}): LinkProps {
  const navigate = useNavigate();
  const path = toRouterPath(to);
  // Hooks run unconditionally; an external href never uses this value.
  const routerHref = useHref(path ?? '.');
  const handleClick = createLinkClickHandler(to, (p) => navigate(p), options);
  return {
    href: path === null ? to : routerHref,
    onClick: (event) => handleClick(event),
  };
}

/**
 * Workspace's `NavLinkComponent` for `AppShell`, `AppSwitcher` and the
 * record patterns, modelled on studio-fripp's and studio-mothership's
 * `RouterLink`.
 *
 * Built on react-router's `Link`, not `NavLink`, on purpose. The shell
 * decides which item is active from its `activeKey` and passes the result as
 * `aria-current` and `className`. v6 `NavLink` would overwrite both from its
 * own URL match: it drops an `aria-current` the shell set when the route
 * doesn't match, adds `aria-current="page"` when it does, and appends an
 * `active` class. `Link` passes every prop through, so the shell's active
 * state is what renders.
 *
 * It forwards its ref to the anchor (collapsed-rail items are tooltip
 * triggers positioned from that ref) and maps `href` to the router's `to`. An
 * external href renders a plain `<a>`, so it is never client-routed.
 */
export const RouterNavLink: NavLinkComponent = forwardRef<HTMLAnchorElement, NavLinkProps>(
  function RouterNavLink({ href, ...props }, ref) {
    const path = toRouterPath(href);
    if (path === null) return <a ref={ref} href={href} {...props} />;
    return <Link ref={ref} to={path} {...props} />;
  },
);
